import React, { useState } from 'react';
import { ChevronDown, RotateCcw, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@grofers/dock-design-kit';
import { Skeleton } from '../../../ComponentsV1/Skeleton/Skeleton';
import { copyToClipboard } from '../../../commons/CommonMethods';

const GUIDANCE_STATUS = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/**
 * Shown once the agent hits "Cannot Verify" on step 3 - appended BELOW the
 * step accordion (Figma node 2219-41284), not a full-screen takeover: the
 * step list stays visible (steps 1-3 completed, step 4 "Resolution" stays
 * locked/pending - there's nothing to resolve once verification has
 * failed, so it's never reachable) with this card and its action rendered
 * underneath, the same way step 4's own "Recommended Response" card sits
 * below that step's accordion entry.
 *
 * Polls `cannot_verify_response` (see CopilotView.js), triggered
 * server-side by `submit_verification`'s own `can_verify: false` call, for
 * a policy-grounded, ready-to-send customer message. The canned-response
 * dropdown the Figma's response card also carries is out of scope here -
 * `cannot_verify_response` only ever resolves a single drafted message, not
 * a list of choices to pick from.
 *
 * The card itself is collapsible, like a dropdown - starts expanded,
 * auto-collapses once the agent copies the message (it's served its
 * purpose), and the header row toggles it open/closed afterward.
 *
 * The header ("Recommended Response" + sparkle) is static copy, not data -
 * it doesn't need to shimmer while the poll is still PENDING, only the
 * message body below it does.
 */
const CannotVerifyResponse = ({
  message = '',
  guidanceStatus = null,
  onRetry = () => {},
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const isLoading =
    guidanceStatus === GUIDANCE_STATUS.PENDING || !guidanceStatus;

  // `copyToClipboard` can reject (e.g. NotAllowedError - denied without
  // focus, or under a restrictive Permissions-Policy) even though it
  // silently no-ops rather than throwing for its own `!document.hasFocus()`
  // check - catch here so a denied write doesn't leave the agent with no
  // feedback at all, and doesn't surface as an unhandled rejection.
  const handleCopy = async () => {
    try {
      await copyToClipboard(message);
      setIsCopied(true);
      setIsExpanded(false);
      toast.success('Copied!');
    } catch (error) {
      toast.error('Could not copy to clipboard, please copy manually');
    }
  };

  return (
    <div className='tw-flex tw-flex-col tw-gap-[8px] tw-items-end tw-w-full tw-rounded-[12px] tw-bg-[#f4f3ff] tw-p-[12px]'>
      <div className='tw-flex tw-flex-col tw-gap-[4px] tw-w-full'>
        <div
          role='button'
          tabIndex={0}
          onClick={() => setIsExpanded((prev) => !prev)}
          className='tw-flex tw-items-center tw-justify-between tw-w-full tw-cursor-pointer'
        >
          <div className='tw-flex tw-items-center tw-gap-[4px]'>
            <Sparkles size={20} className='tw-text-[#5925dc]' />
            <p className='tw-text-[12px] tw-leading-tight tw-font-regular tw-text-[#5925dc] tw-m-0'>
              Recommended Response
            </p>
          </div>
          <ChevronDown
            size={16}
            className={`tw-text-[#5925dc] tw-shrink-0 tw-transition-transform tw-duration-200 ${
              isExpanded ? 'tw-rotate-180' : ''
            }`}
          />
        </div>

        {isExpanded && (
          <div className='tw-flex tw-flex-col tw-gap-[8px] tw-items-end tw-w-full tw-pt-[6px]'>
            {isLoading && (
              <Skeleton
                className='tw-w-full'
                rows={[{ cols: 1, height: '3rem' }]}
              />
            )}

            {guidanceStatus === GUIDANCE_STATUS.FAILED && (
              <>
                <p className='tw-text-[12px] tw-leading-tight tw-font-regular tw-text-red-500 tw-m-0 tw-w-full'>
                  Could not generate a recommended response. Please try again.
                </p>
                <Button
                  variant='secondary'
                  buttonType='default'
                  size='sm'
                  shape='square'
                  className='!tw-border-red-500 !tw-text-red-500'
                  onClick={onRetry}
                >
                  <span className='tw-flex tw-items-center tw-gap-1'>
                    <RotateCcw size={14} />
                    Retry
                  </span>
                </Button>
              </>
            )}

            {guidanceStatus === GUIDANCE_STATUS.COMPLETED && (
              <p className='tw-text-[12px] tw-leading-tight tw-font-regular tw-text-[#4d5761] tw-m-0 tw-w-full tw-whitespace-pre-wrap'>
                {message}
              </p>
            )}

            {guidanceStatus !== GUIDANCE_STATUS.FAILED && (
              <Button
                variant='secondary'
                buttonType='default'
                size='sm'
                shape='square'
                leftIcon={isCopied ? 'check' : 'copy'}
                disabled={isLoading}
                onClick={handleCopy}
              >
                {isCopied ? 'Copied' : 'Copy'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CannotVerifyResponse;
